import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SignaturePad from 'signature_pad';
import './SignaturePadField.css';

interface Props {
  value?: string | null;
  disabled?: boolean;
  onChange?: (dataUrl: string) => void;
  maxKb?: number;
  label?: string;
}

function resizeToJpeg(dataUrl: string, maxKb: number): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxW = 640;
      const scale = Math.min(1, maxW / Math.max(1, img.width));
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      let quality = 0.72;
      let out = canvas.toDataURL('image/jpeg', quality);
      while (out.length * 0.75 > maxKb * 1024 && quality > 0.35) {
        quality -= 0.1;
        out = canvas.toDataURL('image/jpeg', quality);
      }
      resolve(out);
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

function SignaturePadField({
  value,
  disabled,
  onChange,
  maxKb = 80,
  label = '서명',
}: Props) {
  const [open, setOpen] = useState(false);

  if (disabled) {
    return (
      <div className="sig-pad">
        <div className="sig-pad__toolbar">
          <span>{label}</span>
        </div>
        <div className="sig-pad__trigger sig-pad__trigger--readonly">
          {value ? (
            <img src={value} alt={label} className="sig-pad__preview" />
          ) : (
            <p className="sig-pad__empty">서명 없음</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="sig-pad">
      <div className="sig-pad__toolbar">
        <span>{label}</span>
        {value ? (
          <button
            type="button"
            className="ncr-btn is-danger"
            onClick={() => onChange?.('')}
          >
            지우기
          </button>
        ) : null}
      </div>
      <button
        type="button"
        className={`sig-pad__trigger${value ? ' has-value' : ''}`}
        onClick={() => setOpen(true)}
      >
        {value ? (
          <img src={value} alt={label} className="sig-pad__preview" />
        ) : (
          <span className="sig-pad__cta">클릭하여 서명하기</span>
        )}
      </button>
      <p className="sig-pad__hint">넓은 화면에서 서명할 수 있습니다.</p>
      {open
        ? createPortal(
            <SignaturePadModal
              label={label}
              initialValue={value}
              maxKb={maxKb}
              onClose={() => setOpen(false)}
              onConfirm={(dataUrl) => {
                onChange?.(dataUrl);
                setOpen(false);
              }}
            />,
            document.body,
          )
        : null}
    </div>
  );
}

function SignaturePadModal({
  label,
  initialValue,
  maxKb,
  onClose,
  onConfirm,
}: {
  label: string;
  initialValue?: string | null;
  maxKb: number;
  onClose: () => void;
  onConfirm: (dataUrl: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const padRef = useRef<SignaturePad | null>(null);
  const loadingRef = useRef(false);

  const fit = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    const pad = padRef.current;
    if (!canvas || !wrap || !pad) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = wrap.clientWidth || 640;
    const height = Math.max(280, Math.min(420, Math.round(width * 0.45)));
    const data = !pad.isEmpty() ? pad.toData() : null;
    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    pad.clear();
    if (data && data.length) {
      pad.fromData(data);
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(15, 23, 42)',
      minWidth: 1.4,
      maxWidth: 3.2,
    });
    padRef.current = pad;

    const onResize = () => fit();
    fit();
    if (initialValue?.startsWith('data:image')) {
      loadingRef.current = true;
      void pad.fromDataURL(initialValue).finally(() => {
        loadingRef.current = false;
      });
    }

    window.addEventListener('resize', onResize);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = prevOverflow;
      pad.off();
      padRef.current = null;
    };
  }, [fit, initialValue]);

  const clear = () => {
    padRef.current?.clear();
  };

  const confirm = async () => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) {
      onConfirm('');
      return;
    }
    const raw = pad.toDataURL('image/png');
    const compressed = await resizeToJpeg(raw, maxKb);
    onConfirm(compressed);
  };

  return (
    <div
      className="sig-pad-modal__backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="sig-pad-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${label} 작성`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sig-pad-modal__head">
          <div>
            <h3>{label}</h3>
            <p>손가락 또는 마우스로 서명해 주세요.</p>
          </div>
          <button
            type="button"
            className="sig-pad-modal__close"
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>
        </div>
        <div className="sig-pad-modal__canvas-wrap" ref={wrapRef}>
          <canvas ref={canvasRef} className="sig-pad-modal__canvas" />
        </div>
        <div className="sig-pad-modal__actions">
          <button type="button" className="ncr-btn is-secondary" onClick={clear}>
            다시 쓰기
          </button>
          <button type="button" className="ncr-btn is-secondary" onClick={onClose}>
            취소
          </button>
          <button type="button" className="ncr-btn is-primary" onClick={() => void confirm()}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
}

export default SignaturePadField;
